import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseURL } from "../../../../Services/api/baseURL";
import { useNavigate, useParams } from "react-router";
import Loader from "../../../Loader/Loader";
import moment from "moment";
import { ArrowLeftCircle } from "react-feather";
import Pagination from "../../../CustomPagination/Pagination";

const InventoryLog = () => {
  const { id } = useParams();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // frontend pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    product: "",
    category: "",
    subcategory: "",
    reasonForInventory: "",
    performedBy: "",
    updatedAt: "",
  });

  const uniqueValues = (fieldPath) => {
    const keys = fieldPath.split(".");
    return [
      ...new Set(
        inventory
          .map((item) => {
            let value = item;
            for (const key of keys) {
              value = value?.[key];
              if (value == null) break;
            }
            if (fieldPath === "reason" && typeof value === "string") {
              if (value.startsWith("Order")) {
                value = "Order";
              }
            }
            return value ?? null;
          })
          .filter((v) => v !== null)
      ),
    ];
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
    setPage(1);
  };

  const normalizeReason = (reason) => {
    if (typeof reason === "string" && reason.startsWith("Order")) {
      return "Order";
    }
    return reason;
  };

  // filter locally
  const filteredInventory = inventory.filter((record) => {
    return (
      (filters.product === "" ||
        record.product_id?.productName === filters.product) &&
      (filters.category === "" ||
        record.category_id?.collection_name === filters.category) &&
      (filters.subcategory === "" ||
        record.sub_category_id?.sub_collection_name === filters.subcategory) &&
      (filters.reasonForInventory === "" ||
        normalizeReason(record.reason) === filters.reasonForInventory) &&
      (filters.performedBy === "" ||
        record.performed_by === filters.performedBy) &&
      (filters.updatedAt === "" ||
        moment(record.updatedAt).format("DD/MM/YYYY") === filters.updatedAt)
    );
  });

  // frontend pagination slice
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedInventory = filteredInventory.slice(startIndex, endIndex);

  const totalPages = Math.ceil(filteredInventory.length / limit);

  // ── Export to CSV ──────────────────────────────────────────────────────────
  const exportToCSV = () => {
    const headers = [
      "Product",
      "Category",
      "Subcategory",
      "Previous Qty",
      "Qty Changed",
      "New Qty",
      "Reason",
      "Updated By",
      "Updated At",
    ];

    const rows = filteredInventory.map((record) => {
      const isPositive = record.quantity_changed > 0;
      const currentQty = record.previous_quantity + record.quantity_changed;
      return [
        record.product_id?.productName || "NA",
        record.category_id?.collection_name || "NA",
        record.sub_category_id?.sub_collection_name || "NA",
        record.previous_quantity,
        `${isPositive ? "+" : "-"}${Math.abs(record.quantity_changed)}`,
        currentQty,
        record.reason || "NA",
        record.performed_by || "NA",
        moment(record.updatedAt).format("DD/MM/YYYY hh:mm A"),
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const storeName =
      filteredInventory[0]?.store_id?.storeName || "inventory";
    const dateStr = moment().format("YYYY-MM-DD");
    link.download = `${storeName}_inventory_log_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const params = {};
      if (startDate && endDate) {
        params.start_date = moment(startDate).startOf("day").toISOString();
        params.end_date = moment(endDate).endOf("day").toISOString();
      }

      const response = await axios.get(
        `${baseURL}/api/admin/get-stock-inventory/${id}`,
        {
          headers: { Authorization: `${token}` },
          params,
        }
      );

      setInventory(response.data.data.data || []);
    } catch (error) {
      console.error("Error fetching stock data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && !endDate) return;
    fetchData();
  }, [id, startDate, endDate]);

  return (
    <div className="p-4">
      <button onClick={() => navigate(-1)} className="">
        <ArrowLeftCircle className="text-[#d3178a]" />
      </button>

      {!loading ? (
        inventory.length > 0 ? (
          <>
            <div className="flex justify-between">
              <h4 className="text-2xl font-bold mb-6">
                Inventory Log for store :{" "}
                {filteredInventory[0]?.store_id?.storeName}
              </h4>

              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                  className="border p-1 rounded"
                  max={new Date().toISOString().split("T")[0]}
                  placeholder="Start Date"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  className="border p-1 rounded"
                  max={new Date().toISOString().split("T")[0]}
                  placeholder="End Date"
                />
                {(startDate || endDate) && (
                  <button
                    className="bg-red-400 text-white px-3 py-1 rounded"
                    onClick={() => {
                      setEndDate(null);
                      setStartDate(null);
                      setPage(1);
                    }}
                  >
                    reset
                  </button>
                )}

                {/* ── Export Button ── */}
                <button
                  onClick={exportToCSV}
                  disabled={filteredInventory.length === 0}
                  className="flex items-center gap-1 bg-[#d3178a] hover:bg-[#b5136f] disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1 rounded transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                    />
                  </svg>
                  Export
                </button>
              </div>
            </div>

            <div className="overflow-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr>
                    {/* Product */}
                    <th className="px-2 py-2 border-b">
                      <select
                        name="product"
                        value={filters.product}
                        onChange={handleFilterChange}
                        className="w-full"
                      >
                        <option value="">Products</option>
                        {uniqueValues("product_id.productName").map(
                          (value, index) => (
                            <option key={index} value={value}>
                              {value}
                            </option>
                          )
                        )}
                      </select>
                    </th>

                    {/* Category */}
                    <th className="px-2 py-2 border-b">
                      <select
                        name="category"
                        value={filters.category}
                        onChange={handleFilterChange}
                        className="w-full"
                      >
                        <option value="">Categories</option>
                        {uniqueValues("category_id.collection_name").map(
                          (value, index) => (
                            <option key={index} value={value}>
                              {value}
                            </option>
                          )
                        )}
                      </select>
                    </th>

                    {/* Subcategory */}
                    <th className="px-2 py-2 border-b">
                      <select
                        name="subcategory"
                        value={filters.subcategory}
                        onChange={handleFilterChange}
                        className="w-full"
                      >
                        <option className="text-xs" value="">
                          Subcategories
                        </option>
                        {uniqueValues(
                          "sub_category_id.sub_collection_name"
                        ).map((value, index) => (
                          <option key={index} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </th>

                    {/* Previous Quantity */}
                    <th className="px-2 py-2 border-b">Previous Qty</th>

                    {/* Quantity Changed */}
                    <th className="px-2 py-2 border-b">Qty Changed</th>

                    {/* New Quantity */}
                    <th className="px-2 py-2 border-b">New Qty</th>

                    {/* Reason */}
                    <th className="px-2 py-2 border-b">
                      <select
                        name="reasonForInventory"
                        value={filters.reasonForInventory}
                        onChange={handleFilterChange}
                        className="w-full"
                      >
                        <option value="">Reason</option>
                        {uniqueValues("reason").map((value, index) => (
                          <option key={index} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </th>

                    {/* Updated By */}
                    <th className="px-2 py-2 border-b">
                      <select
                        name="performedBy"
                        value={filters.performedBy}
                        onChange={handleFilterChange}
                        className="w-full"
                      >
                        <option value="">Updated By</option>
                        {uniqueValues("performed_by").map((value, index) => (
                          <option key={index} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </th>

                    {/* Updated At */}
                    <th className="px-2 py-2 text-center border-b">
                      Updated At
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedInventory.map((record) => {
                    const isPositive = record.quantity_changed > 0;
                    const currentQty =
                      record.previous_quantity + record.quantity_changed;
                    return (
                      <tr key={record._id} className="hover:bg-gray-100">
                        <td className="px-4 py-2 border-b">
                          {record.product_id?.productName || "NA"}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {record.category_id?.collection_name || "NA"}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {record.sub_category_id?.sub_collection_name || "NA"}
                        </td>

                        <td className="px-4 py-2 border-b">
                          {record.previous_quantity}
                        </td>

                        <td className="px-4 py-2 border-b">
                          <span
                            className={`font-bold ${
                              isPositive ? "text-green-600" : "text-red-600"
                            }`}
                          >
                            {isPositive ? "+" : "-"}
                            {Math.abs(record.quantity_changed)}
                          </span>
                        </td>

                        <td className="px-4 py-2 border-b">{currentQty}</td>

                        <td className="px-4 py-2 border-b">
                          {record.reason || "NA"}
                        </td>
                        <td className="px-4 py-2 border-b">
                          {record.performed_by || "NA"}
                        </td>
                        <td className="px-4 py-2 text-center border-b">
                          {moment(record.updatedAt).format(
                            "DD/MM/YYYY hh:mm A"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <Pagination
                page={page}
                totalPages={totalPages}
                setPage={setPage}
                limit={limit}
                setLimit={setLimit}
                totalDocs={filteredInventory.length}
              />
            </div>
          </>
        ) : (
          <p className="text-2xl font-bold mb-4">No Inventory Records Found.</p>
        )
      ) : (
        <Loader />
      )}
    </div>
  );
};

export default InventoryLog;



///Before adding export button
// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { baseURL } from "../../../../Services/api/baseURL";
// import { useNavigate, useParams } from "react-router";
// import Loader from "../../../Loader/Loader";
// import moment from "moment";
// import { ArrowLeftCircle } from "react-feather";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import Pagination from "../../../CustomPagination/Pagination";

// const InventoryLog = () => {
//   const { id } = useParams();
//   const [inventory, setInventory] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);

//   // frontend pagination
//   const [page, setPage] = useState(1);
//   const [limit, setLimit] = useState(10);

//   const navigate = useNavigate();

//   const [filters, setFilters] = useState({
//     product: "",
//     category: "",
//     subcategory: "",
//     reasonForInventory: "",
//     performedBy: "",
//     updatedAt: "",
//   });


//   const uniqueValues = (fieldPath) => {
//     const keys = fieldPath.split(".");
//     return [
//       ...new Set(
//         inventory
//           .map((item) => {
//             let value = item;
//             for (const key of keys) {
//               value = value?.[key];
//               if (value == null) break;
//             }
//             // special case for reason
//             if (fieldPath === "reason" && typeof value === "string") {
//               if (value.startsWith("Order")) {
//                 value = "Order";
//               }
//             }
//             return value ?? null;
//           })
//           .filter((v) => v !== null)
//       ),
//     ];
//   };

//   const handleFilterChange = (e) => {
//     setFilters({
//       ...filters,
//       [e.target.name]: e.target.value,
//     });
//     setPage(1); // reset to first page when filter changes
//   };

//   const normalizeReason = (reason) => {
//     if (typeof reason === "string" && reason.startsWith("Order")) {
//       return "Order";
//     }
//     return reason;
//   };

//   // filter locally
//   const filteredInventory = inventory.filter((record) => {
//     return (
//       // record.store_id?._id === id && 
//       (filters.product === "" ||
//         record.product_id?.productName === filters.product) &&
//       (filters.category === "" ||
//         record.category_id?.collection_name === filters.category) &&
//       (filters.subcategory === "" ||
//         record.sub_category_id?.sub_collection_name === filters.subcategory) &&
//       (filters.reasonForInventory === "" ||
//         normalizeReason(record.reason) === filters.reasonForInventory) &&
//       (filters.performedBy === "" ||
//         record.performed_by === filters.performedBy) &&
//       (filters.updatedAt === "" ||
//         moment(record.updatedAt).format("DD/MM/YYYY") === filters.updatedAt)
//     );
//   });

//   // frontend pagination slice
//   const startIndex = (page - 1) * limit;
//   const endIndex = startIndex + limit;
//   const paginatedInventory = filteredInventory.slice(startIndex, endIndex);

//   const totalPages = Math.ceil(filteredInventory.length / limit);

//   const fetchData = async () => {
//     setLoading(true);
//     try {
//       const token = JSON.parse(localStorage.getItem("token"));
//       const params = {};
// if (startDate && endDate) {
//   params.start_date = moment(startDate).startOf("day").toISOString();
//   params.end_date   = moment(endDate).endOf("day").toISOString();
// }


//       const response = await axios.get(
//         `${baseURL}/api/admin/get-stock-inventory/${id}`,
//         {
//           headers: { Authorization: `${token}` },
//           params,
//         }
//       );

//       setInventory(response.data.data.data || []);
//     } catch (error) {
//       console.error("Error fetching stock data:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (startDate && !endDate) return; // wait until both selected
//     fetchData();
//   }, [id, startDate, endDate]);

//   return (
//     <div className="p-4">
//       <button onClick={() => navigate(-1)} className="">
//         <ArrowLeftCircle className="text-[#d3178a]" />
//       </button>

//       {!loading ? (
//         inventory.length > 0 ? (
//           <>
//             <div className="flex justify-between">
//               <h4 className="text-2xl font-bold mb-6">
//                 Inventory Log for store :{" "}
//                 {filteredInventory[0]?.store_id?.storeName}
//               </h4>

//               <div className="flex gap-2 items-center">
//                 {/* <DatePicker
//                   className="form-control datepickerr digits w-75 mx-2"
//                   selected={startDate}
//                   onChange={(date) => {
//                     setStartDate(date);
//                     setPage(1);
//                   }}
//                   onKeyDown={(e) => e.preventDefault()}
//                   placeholderText="Start Date"
//                   dateFormat="yyyy-MM-dd"
//                   // isClearable
//                   showIcon
//                 /> */}
//                 <input
//                 type="date"
//                 value={startDate}
//                 onChange={(e)=>{
//                   setStartDate(e.target.value)
//                   setPage(1)
//                 }}
//              className="border p-1 rounded"
//               max={new Date().toISOString().split("T")[0]}
//                 placeholder="Start Date"
//                 />
//                                 <input
//                 type="date"
//                 value={endDate}
//                 onChange={(e)=>{
//                   setEndDate(e.target.value)
//                   setPage(1)
//                 }}
//              className="border p-1 rounded"
//               max={new Date().toISOString().split("T")[0]}
//                 placeholder="End Date"
//                 />
//                 {/* <DatePicker
//                   className="form-control datepickerr digits w-75 mx-2"
//                   selected={endDate}
//                   onChange={(date) => {
//                     setEndDate(date);
//                     setPage(1);
//                   }}
//                   onKeyDown={(e) => e.preventDefault()}
//                   placeholderText="End Date"
//                   dateFormat="yyyy-MM-dd"
//                   // isClearable
//                   showIcon
//                 /> */}
//                 {(startDate || endDate) && (
//                   <button
//                     className="bg-red-400 text-white px-3 py-1 rounded"
//                     onClick={() => {
//                       setEndDate(null);
//                       setStartDate(null);
//                       setPage(1);
//                     }}
//                   >
//                     reset
//                   </button>
//                 )}
//               </div>
//             </div>

//             <div className="overflow-auto">
//               <table className="min-w-full bg-white border border-gray-200">
//                 <thead>
//                   <tr>
//                     {/* Product */}
//                     <th className="px-2 py-2 border-b">
//                       <select
//                         name="product"
//                         value={filters.product}
//                         onChange={handleFilterChange}
//                         className="w-full"
//                       >
//                         <option value="">Products</option>
//                         {uniqueValues("product_id.productName").map(
//                           (value, index) => (
//                             <option key={index} value={value}>
//                               {value}
//                             </option>
//                           )
//                         )}
//                       </select>
//                     </th>

//                     {/* Category */}
//                     <th className="px-2 py-2 border-b">
//                       <select
//                         name="category"
//                         value={filters.category}
//                         onChange={handleFilterChange}
//                         className="w-full"
//                       >
//                         <option value="">Categories</option>
//                         {uniqueValues("category_id.collection_name").map(
//                           (value, index) => (
//                             <option key={index} value={value}>
//                               {value}
//                             </option>
//                           )
//                         )}
//                       </select>
//                     </th>

//                     {/* Subcategory */}
//                     <th className="px-2 py-2 border-b">
//                       <select
//                         name="subcategory"
//                         value={filters.subcategory}
//                         onChange={handleFilterChange}
//                         className="w-full"
//                       >
//                         <option className="text-xs" value="">
//                           Subcategories
//                         </option>
//                         {uniqueValues("sub_category_id.sub_collection_name").map(
//                           (value, index) => (
//                             <option key={index} value={value}>
//                               {value}
//                             </option>
//                           )
//                         )}
//                       </select>
//                     </th>

//                     {/* Previous Quantity */}
//                     <th className="px-2 py-2 border-b">Previous Qty</th>

//                     {/* Quantity Changed */}
//                     <th className="px-2 py-2 border-b">Qty Changed</th>

//                     {/* New Quantity */}
//                     <th className="px-2 py-2 border-b">New Qty</th>

//                     {/* Reason */}
//                     <th className="px-2 py-2 border-b">
//                       <select
//                         name="reasonForInventory"
//                         value={filters.reasonForInventory}
//                         onChange={handleFilterChange}
//                         className="w-full"
//                       >
//                         <option value="">Reason</option>
//                         {uniqueValues("reason").map((value, index) => (
//                           <option key={index} value={value}>
//                             {value}
//                           </option>
//                         ))}
//                       </select>
//                     </th>

//                     {/* Updated By */}
//                     <th className="px-2 py-2 border-b">
//                       <select
//                         name="performedBy"
//                         value={filters.performedBy}
//                         onChange={handleFilterChange}
//                         className="w-full"
//                       >
//                         <option value="">Updated By</option>
//                         {uniqueValues("performed_by").map((value, index) => (
//                           <option key={index} value={value}>
//                             {value}
//                           </option>
//                         ))}
//                       </select>
//                     </th>

//                     {/* Updated At */}
//                     <th className="px-2 py-2 text-center border-b">
//                       Updated At
//                     </th>
//                   </tr>
//                 </thead>

//                 <tbody>
//                   {paginatedInventory.map((record) => {
//                     const isPositive = record.quantity_changed > 0;
//                     const currentQty =
//                       record.previous_quantity + record.quantity_changed;
//                     return (
//                       <tr key={record._id} className="hover:bg-gray-100">
//                         <td className="px-4 py-2 border-b">
//                           {record.product_id?.productName || "NA"}
//                         </td>
//                         <td className="px-4 py-2 border-b">
//                           {record.category_id?.collection_name || "NA"}
//                         </td>
//                         <td className="px-4 py-2 border-b">
//                           {record.sub_category_id?.sub_collection_name || "NA"}
//                         </td>

//                         <td className="px-4 py-2 border-b">
//                           {record.previous_quantity}
//                         </td>

//                         <td className="px-4 py-2 border-b">
//                           <span
//                             className={`font-bold ${
//                               isPositive ? "text-green-600" : "text-red-600"
//                             }`}
//                           >
//                             {isPositive ? "+" : "-"}
//                             {Math.abs(record.quantity_changed)}
//                           </span>
//                         </td>

//                         <td className="px-4 py-2 border-b">{currentQty}</td>

//                         <td className="px-4 py-2 border-b">
//                           {record.reason || "NA"}
//                         </td>
//                         <td className="px-4 py-2 border-b">
//                           {record.performed_by || "NA"}
//                         </td>
//                         <td className="px-4 py-2 text-center border-b">
//                           {moment(record.updatedAt).format(
//                             "DD/MM/YYYY hh:mm A"
//                           )}
//                         </td>
//                       </tr>
//                     );
//                   })}
//                 </tbody>
//               </table>

//               <Pagination
//                 page={page}
//                 totalPages={totalPages}
//                 setPage={setPage}
//                 limit={limit}
//                 setLimit={setLimit}
//                 totalDocs={filteredInventory.length}
//               />
//             </div>
//           </>
//         ) : (
//           <p className="text-2xl font-bold mb-4">No Inventory Records Found.</p>
//         )
//       ) : (
//         <Loader />
//       )}
//     </div>
//   );
// };

// export default InventoryLog;
